/**
 * KEFI Jewellery — Theme JavaScript
 * Security hardened per OWASP Top 10 (2021) and ASVS v4
 *
 * OWASP A03 — Injection/XSS:
 *   - Never use innerHTML with dynamic data; always textContent or DOM APIs
 *   - All user input sanitised before DOM insertion
 *
 * OWASP A04 — Insecure Design:
 *   - Input validated before submission (email format, max length)
 *   - Redirect targets validated against same-origin
 *
 * OWASP A07 — Identification / Auth Failures:
 *   - Honeypot fields on all forms
 *   - Submit buttons disabled after click (prevents double-submit)
 *   - Rate limiting on cart operations
 *
 * OWASP A08 — Software Integrity:
 *   - No eval(), no new Function(), no document.write()
 *   - window.kefi frozen to prevent prototype pollution
 */

(function () {
  'use strict';

  /* ============================================================
     SECURITY UTILITIES
     ============================================================ */

  /**
   * Safe DOM text setter — never use innerHTML for dynamic content.
   *
   * This is deliberately the *only* text-insertion helper in the file. An
   * HTML-entity encoder is not provided on purpose: having one on hand invites
   * "encode then innerHTML", which is the pattern this theme avoids entirely.
   * @param {Element} el
   * @param {string} text
   */
  function safeText(el, text) {
    if (el) el.textContent = String(text);
  }

  /**
   * Validate email format (client-side, defence-in-depth only — server validates too).
   * RFC 5322 simplified pattern.
   */
  function isValidEmail(email) {
    if (typeof email !== 'string') return false;
    if (email.length > 254) return false; // RFC 5321 max
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  /**
   * Honeypot check — if the hidden field has a value, it's a bot.
   * OWASP A07.
   */
  function isBot(form) {
    var trap = form.querySelector('[name="contact[url]"]');
    return trap && trap.value.length > 0;
  }

  /**
   * Rate limiter — prevents rapid repeated API calls.
   * Returns a throttled version of fn that can only fire once per `wait` ms.
   *
   * Leading edge only: calls arriving inside the window are DROPPED, not
   * deferred. That is deliberate for rate limiting (a dropped second click
   * is exactly what stops a double-submit) but wrong for anything tracking a
   * position, where losing the final event leaves the UI stuck describing a
   * state the page is no longer in. Use onFrame for those — see below.
   */
  function throttle(fn, wait) {
    var lastCall = 0;
    return function () {
      var now = Date.now();
      if (now - lastCall < wait) return;
      lastCall = now;
      return fn.apply(this, arguments);
    };
  }

  /**
   * Coalesce a burst of events into one call per animation frame.
   *
   * For scroll/resize handlers that read live geometry. Unlike throttle it
   * never discards the end of a burst: the callback runs after the events it
   * coalesced and re-reads the current position, so the final state is always
   * the one applied. Flick-scrolling to the top can otherwise land its last
   * scroll event inside a throttle window and have it thrown away, leaving
   * (for example) the nav pinned at a scroll offset of zero.
   */
  function onFrame(fn) {
    var scheduled = false;
    return function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        fn();
      });
    };
  }

  /**
   * Motion preference — declared once up front because several features
   * (announcement bar, hero autoplay, scroll animations) all gate on it.
   */
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Show an error message safely (no XSS).
   */
  function showError(el, message) {
    if (!el) return;
    safeText(el, message); // textContent — safe
    el.removeAttribute('hidden');
    el.setAttribute('role', 'alert');
  }

  function hideError(el) {
    if (!el) return;
    el.setAttribute('hidden', '');
    safeText(el, '');
  }

  /* ============================================================
     CART API — authenticated, throttled, CSRF-aware
     ============================================================ */

  /**
   * Make a cart API request.
   * Shopify's cart endpoints don't require explicit CSRF tokens (same-origin
   * cookies + SameSite are the protection). However, we always use 'same-origin'
   * credentials and validate same-origin on any redirect URL.
   */
  function cartFetch(url, method, body) {
    return fetch(url, {
      method: method || 'POST',
      credentials: 'same-origin', // Include session cookie; never 'include' cross-origin
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest' // Tells Shopify this is an AJAX call
      },
      body: body ? JSON.stringify(body) : undefined
    });
  }

  /* ============================================================
     HEADER — SCROLL EFFECT (pin the nav row)

     The announcement bar and logo row scroll away; once they have, the nav
     pins to the top so the menu stays reachable.

     This can't be done with position:sticky. Shopify wraps every section in
     its own .shopify-section div, and a sticky element only sticks within its
     parent's box — that wrapper is exactly the header's height, so a sticky
     header releases the moment it tries to stick and scrolls away anyway.
     So: measure where the nav sits in the document and switch it to fixed.

     Pinning takes the nav out of normal flow, which would collapse the space
     it occupied and jump the page upward — hence the holder, which is given
     the nav's height for exactly as long as the nav is pinned.

     Nav is display:none under 960px (the hamburger takes over), so this is a
     desktop-only concern. Hidden is detected via offsetHeight === 0 rather
     than offsetParent: offsetParent is null for position:fixed elements, so
     once pinned it would read as "hidden" and immediately unpin itself.
     ============================================================ */

  var header    = document.getElementById('site-header');
  var siteNav   = document.getElementById('site-nav');
  var navHolder = document.getElementById('site-nav-holder');

  if (header && siteNav && navHolder) {
    var navPinned = false;

    // display:none -> 0. A pinned (fixed) nav still reports its real height.
    function navHidden() { return siteNav.offsetHeight === 0; }

    function navPinPoint() {
      // Distance from the top of the document to the top of the holder.
      var y = 0, el = navHolder;
      while (el) { y += el.offsetTop; el = el.offsetParent; }
      return y;
    }

    /* Publish the pinned nav's height so anything that has to clear it can
       offset by the real measured value. --header-height can't be used for
       that any more: the header no longer stays on screen, so offsetting by
       it would leave a gap the size of a header that isn't there. */
    function publishNavHeight() {
      document.documentElement.style.setProperty('--nav-height', siteNav.offsetHeight + 'px');
    }

    function syncNavPin() {
      // Hidden (mobile): make sure we never leave it pinned or holding height
      if (navHidden() && !navPinned) return;
      if (navHidden()) {
        siteNav.classList.remove('site-nav--pinned');
        navHolder.style.height = '';
        navPinned = false;
        publishNavHeight();
        return;
      }

      var shouldPin = window.scrollY > navPinPoint();
      if (shouldPin === navPinned) return;

      if (shouldPin) {
        // Capture the height BEFORE pinning, while it still occupies flow
        navHolder.style.height = siteNav.offsetHeight + 'px';
        siteNav.classList.add('site-nav--pinned');
      } else {
        siteNav.classList.remove('site-nav--pinned');
        navHolder.style.height = '';
      }
      navPinned = shouldPin;
    }

    /* How much of the first screen is used up before the hero starts.
       .hero-slider__media subtracts this from the viewport height so the
       banner ends exactly on the fold.

       Measured rather than assumed: --header-height was a hardcoded 140px,
       but the logo height is a theme setting, so the real header moves
       whenever a merchant changes it — and the hero then either overflowed
       past the fold or stopped short of it.

       offsetTop is walked up the chain rather than using
       getBoundingClientRect, so the number is the distance from the top of
       the DOCUMENT and does not change as the page scrolls. It is also in
       unscaled CSS px, which is what the calc() in the stylesheet expects. */
    function publishHeroOffset() {
      /* Whichever section asked to fill the screen. [data-fill-viewport] is
         set by any section with the option ticked (Lookbook today); the hero
         slider is the fallback so it keeps working if it is used instead. */
      var el = document.querySelector('[data-fill-viewport]')
            || document.getElementById('hero-slider');
      if (!el) return;
      var y = 0;
      while (el) { y += el.offsetTop; el = el.offsetParent; }
      document.documentElement.style.setProperty('--fill-offset', y + 'px');
    }

    /* onFrame, not throttle: a throttled scroll handler can drop the last
       event of a fast flick, which strands the nav pinned at the top of the
       page — overlapping the logo and sliding under the announcement bar. */
    window.addEventListener('scroll', onFrame(syncNavPin), { passive: true });
    window.addEventListener('resize', onFrame(function () {
      // Re-measure: the pin point moves when the header reflows
      if (navPinned) {
        siteNav.classList.remove('site-nav--pinned');
        navHolder.style.height = '';
        navPinned = false;
      }
      publishNavHeight();
      publishHeroOffset();
      syncNavPin();
    }));
    publishNavHeight();
    publishHeroOffset();
    syncNavPin();

    /* Re-measure once everything above the hero has settled. The logo is an
       image: until it loads the header is shorter than its final height, so
       an offset taken at DOMContentLoaded alone would leave the hero running
       past the fold by exactly the logo's height. */
    window.addEventListener('load', publishHeroOffset);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(publishHeroOffset).catch(function () {});
    }
  }

  /* ============================================================
     ANNOUNCEMENT BAR — rotating messages
     Settings come from the section's data attributes, so merchants control
     rotation from the theme editor.
     ============================================================ */

  var annBar = document.getElementById('announcement-bar');
  if (annBar) {
    var annSlides = Array.prototype.slice.call(
      annBar.querySelectorAll('.announcement-bar__text')
    );

    if (annSlides.length > 1) {
      var annIdx = 0;
      var annTimer = null;
      var annAuto = annBar.dataset.autorotate === 'true';
      // Interval is authored in seconds; clamp so a bad value can't busy-loop
      var annInterval = Math.max(3, parseInt(annBar.dataset.interval, 10) || 5) * 1000;

      var showAnnouncement = function (n) {
        annIdx = ((n % annSlides.length) + annSlides.length) % annSlides.length;
        annSlides.forEach(function (slide, i) {
          slide.classList.toggle('active', i === annIdx);
        });
      };

      var stopAnnouncementRotation = function () {
        if (annTimer) clearInterval(annTimer);
        annTimer = null;
      };

      var startAnnouncementRotation = function () {
        if (!annAuto || prefersReducedMotion.matches) return;
        stopAnnouncementRotation();
        annTimer = setInterval(function () { showAnnouncement(annIdx + 1); }, annInterval);
      };

      var annPrev = document.getElementById('announcement-prev');
      var annNext = document.getElementById('announcement-next');

      if (annPrev) {
        annPrev.addEventListener('click', function () {
          showAnnouncement(annIdx - 1);
          startAnnouncementRotation();
        });
      }
      if (annNext) {
        annNext.addEventListener('click', function () {
          showAnnouncement(annIdx + 1);
          startAnnouncementRotation();
        });
      }

      // Pause while hovered or keyboard-focused so messages stay readable
      annBar.addEventListener('mouseenter', stopAnnouncementRotation);
      annBar.addEventListener('mouseleave', startAnnouncementRotation);
      annBar.addEventListener('focusin', stopAnnouncementRotation);
      annBar.addEventListener('focusout', startAnnouncementRotation);

      showAnnouncement(0);
      startAnnouncementRotation();
    }
  }

  /* ============================================================
     MOBILE NAV
     ============================================================ */

  var hamburger    = document.getElementById('hamburger-btn');
  var mobileNav    = document.getElementById('mobile-nav');
  var mobileOverlay = document.getElementById('mobile-overlay');
  var mobileClose  = document.getElementById('mobile-nav-close');

  function openMobileNav() {
    if (!mobileNav) return;
    mobileNav.classList.add('active');
    mobileNav.setAttribute('aria-hidden', 'false');
    if (mobileOverlay) { mobileOverlay.classList.add('active'); mobileOverlay.setAttribute('aria-hidden', 'false'); }
    if (hamburger) hamburger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    // Move focus into nav for accessibility
    var first = mobileNav.querySelector('a, button');
    if (first) setTimeout(function () { first.focus(); }, 50);
  }

  function closeMobileNav() {
    if (!mobileNav) return;
    mobileNav.classList.remove('active');
    mobileNav.setAttribute('aria-hidden', 'true');
    if (mobileOverlay) { mobileOverlay.classList.remove('active'); mobileOverlay.setAttribute('aria-hidden', 'true'); }
    if (hamburger) { hamburger.setAttribute('aria-expanded', 'false'); hamburger.focus(); }
    document.body.style.overflow = '';
  }

  if (hamburger) hamburger.addEventListener('click', openMobileNav);
  if (mobileClose) mobileClose.addEventListener('click', closeMobileNav);
  if (mobileOverlay) mobileOverlay.addEventListener('click', closeMobileNav);

  // Mobile submenu toggles
  document.querySelectorAll('.mobile-nav__toggle').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      var targetId = btn.getAttribute('aria-controls');
      var submenu = targetId ? document.getElementById(targetId) : null;
      btn.setAttribute('aria-expanded', !expanded);
      if (submenu) submenu.setAttribute('aria-hidden', expanded);
    });
  });

  /* ============================================================
     COUNTRY / CURRENCY SELECTOR

     Progressive enhancement over snippets/currency-selector.liquid: the
     markup is a real {% form 'localization' %} that already works via its
     <noscript> submit button, so this only removes the extra click.

     There are two instances on the page (header and nav drawer), hence
     querySelectorAll — binding by id would only ever wire up one of them.
     ============================================================ */

  document.querySelectorAll('[data-localization-select]').forEach(function (select) {
    select.addEventListener('change', function () {
      var form = select.closest('form');
      if (!form) return;
      // requestSubmit runs validation and fires the submit event; older
      // browsers fall back to the blunt version.
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    });
  });

  /* ============================================================
     SEARCH OVERLAY
     ============================================================ */

  /* Search and cart each have TWO triggers — one in the header, one in the
     pinned nav row — so they're bound by data attribute rather than by id.
     Whichever was clicked is remembered, so closing returns focus to the
     control the customer actually used rather than always the header's. */
  var searchTriggers = document.querySelectorAll('[data-search-open]');
  var searchOverlay  = document.getElementById('search-overlay');
  var searchClose    = document.getElementById('search-close');
  var searchInput    = document.getElementById('search-input');
  var lastSearchTrigger = null;

  /** Focusable in practice? visibility:hidden hides the pinned copies. */
  function isReachable(el) {
    return !!el && window.getComputedStyle(el).visibility !== 'hidden';
  }

  /** Focus the trigger that opened this, falling back to any visible one. */
  function refocusTrigger(list, preferred) {
    if (isReachable(preferred)) { preferred.focus(); return; }
    for (var i = 0; i < list.length; i++) {
      if (isReachable(list[i])) { list[i].focus(); return; }
    }
  }

  /* No scroll lock: this is a strip under the header, not a modal covering
     the page. Freezing the body would strand a customer who opened search by
     accident, and there is nothing behind it to protect. */
  function openSearch() {
    if (!searchOverlay) return;
    searchOverlay.classList.add('active');
    searchTriggers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    if (searchInput) setTimeout(function () { searchInput.focus(); }, 80);
  }
  function closeSearch() {
    // Escape calls every close handler blind, so bail unless this one is
    // actually open — otherwise refocusTrigger would yank focus to the
    // search button on any stray Escape press.
    if (!searchOverlay || !searchOverlay.classList.contains('active')) return;
    searchOverlay.classList.remove('active');
    searchTriggers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    refocusTrigger(searchTriggers, lastSearchTrigger);
    // Lets the predictive-search block below clear its panel without this
    // function needing to know that block exists.
    searchOverlay.dispatchEvent(new CustomEvent('kefi:search-closed'));
  }

  searchTriggers.forEach(function (btn) {
    btn.addEventListener('click', function () { lastSearchTrigger = btn; openSearch(); });
  });
  if (searchClose) searchClose.addEventListener('click', closeSearch);

  /* ------------------------------------------------------------
     PREDICTIVE SEARCH

     Results come from the Section Rendering API, not the JSON endpoint:
     sections/predictive-search.liquid renders them with the money filter and
     product-card, so prices are correct in whatever currency the buyer is
     browsing and the cards match the collection grid. Formatting money in JS
     would have to re-derive the active market's rules, and would drift.

     Everything below exists because typing fires a lot of events:

       - DEBOUNCE so a five-letter word is one request, not five.
       - A REQUEST TOKEN, because responses can land out of order. Without it
         a slow reply for "ea" can overwrite a fast one for "earring" and the
         panel ends up showing results for a prefix the field no longer
         contains. Only the newest token is allowed to paint.
       - AbortController to drop in-flight work that is already stale.
     ------------------------------------------------------------ */
  var searchResults = searchOverlay ? searchOverlay.querySelector('[data-search-results]') : null;

  if (searchInput && searchResults && window.kefi && window.kefi.routes && window.kefi.routes.search) {
    var SEARCH_DEBOUNCE = 220;   // ms — below this, typing outruns the network
    var SEARCH_MIN = 2;          // one letter matches most of the catalogue
    var searchTimer = null;
    var searchToken = 0;
    var searchAbort = null;

    var hideResults = function () {
      searchResults.hidden = true;
      searchResults.innerHTML = '';
      searchInput.setAttribute('aria-expanded', 'false');
    };

    var runSearch = function (term) {
      var token = ++searchToken;
      if (searchAbort) searchAbort.abort();
      searchAbort = (typeof AbortController !== 'undefined') ? new AbortController() : null;

      var url = window.kefi.routes.search +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product,collection' +
        '&resources[limit]=8' +
        '&section_id=predictive-search';

      fetch(url, searchAbort ? { signal: searchAbort.signal } : undefined)
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(function (html) {
          // A newer keystroke already started; this reply is stale.
          if (token !== searchToken) return;
          searchResults.innerHTML = html;
          searchResults.hidden = false;
          searchInput.setAttribute('aria-expanded', 'true');
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (token !== searchToken) return;
          // Enter still submits to the real search page, so failing quietly
          // leaves the customer with a working route rather than an error.
          hideResults();
        });
    };

    searchInput.addEventListener('input', function () {
      var term = searchInput.value.trim();
      clearTimeout(searchTimer);

      if (term.length < SEARCH_MIN) {
        if (searchAbort) searchAbort.abort();
        searchToken++;             // invalidate anything already in flight
        hideResults();
        return;
      }
      searchTimer = setTimeout(function () { runSearch(term); }, SEARCH_DEBOUNCE);
    });

    // Tabs are re-rendered with every response, so the listener is delegated
    // from the container rather than bound to buttons that keep being replaced.
    searchResults.addEventListener('click', function (e) {
      var tab = e.target.closest ? e.target.closest('[data-search-tab]') : null;
      if (!tab) return;
      var name = tab.getAttribute('data-search-tab');

      searchResults.querySelectorAll('[data-search-tab]').forEach(function (t) {
        var on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      searchResults.querySelectorAll('[data-search-panel]').forEach(function (p) {
        p.hidden = p.getAttribute('data-search-panel') !== name;
      });
    });

    // Clear the panel on close so reopening doesn't flash the last search
    searchOverlay.addEventListener('kefi:search-closed', hideResults);
  }

  /* ============================================================
     CART DRAWER
     ============================================================ */

  // Two triggers and two badges (header + pinned nav) — see the search block
  var cartTriggers = document.querySelectorAll('[data-cart-open]');
  var cartCounts   = document.querySelectorAll('[data-cart-count]');
  var cartOverlay  = document.getElementById('cart-overlay');
  var cartDrawer   = document.getElementById('cart-drawer');
  var cartClose    = document.getElementById('cart-close');
  var lastCartTrigger = null;

  function openCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('active');
    cartDrawer.setAttribute('aria-hidden', 'false');
    if (cartOverlay) cartOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    var firstFocusable = cartDrawer.querySelector('button, a, input');
    if (firstFocusable) setTimeout(function () { firstFocusable.focus(); }, 50);
  }
  function closeCart() {
    // See closeSearch — only act if this drawer is the one that's open
    if (!cartDrawer || !cartDrawer.classList.contains('active')) return;
    cartDrawer.classList.remove('active');
    cartDrawer.setAttribute('aria-hidden', 'true');
    if (cartOverlay) cartOverlay.classList.remove('active');
    document.body.style.overflow = '';
    refocusTrigger(cartTriggers, lastCartTrigger);
  }

  cartTriggers.forEach(function (btn) {
    btn.addEventListener('click', function () { lastCartTrigger = btn; openCart(); });
  });
  if (cartClose) cartClose.addEventListener('click', closeCart);
  if (cartOverlay) cartOverlay.addEventListener('click', closeCart);

  // Update every cart badge safely (textContent only — no innerHTML)
  function updateCartCount(count) {
    var n = parseInt(count, 10) || 0;
    cartCounts.forEach(function (el) {
      safeText(el, n > 0 ? String(n) : '');
      el.style.display = n > 0 ? '' : 'none';
    });
    cartTriggers.forEach(function (btn) {
      btn.setAttribute('aria-label', 'Cart (' + n + ' items)');
    });
  }

  /**
   * Re-render the cart drawer from the Section Rendering API.
   *
   * The drawer is server-rendered at page load, so without this it would show
   * stale contents after an add-to-cart. We request only the cart-drawer
   * markup and swap in the parsed <div id="cart-body"> subtree.
   *
   * OWASP A03: the response is Shopify-generated HTML for our own section, but
   * it is still parsed into a detached document and moved node-by-node rather
   * than assigned via innerHTML on a live element.
   */
  var CART_DRAWER_SECTION = 'cart-drawer';

  /**
   * Total units in the drawer — the sum of every line's quantity, not the
   * number of lines. Two lines of 3 and 4 must report 7, matching Shopify's
   * cart.item_count, so the badge never disagrees with the cart page.
   */
  function countItemsInDrawer() {
    var total = 0;
    document.querySelectorAll('#cart-body .cart-item__qty span').forEach(function (el) {
      total += parseInt(el.textContent, 10) || 0;
    });
    return total;
  }

  /** Swap fresh drawer markup into the live DOM. */
  function applyCartDrawerHTML(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');

    var freshBody = parsed.getElementById('cart-body');
    var liveBody = document.getElementById('cart-body');
    if (freshBody && liveBody) {
      while (liveBody.firstChild) liveBody.removeChild(liveBody.firstChild);
      while (freshBody.firstChild) liveBody.appendChild(freshBody.firstChild);
    }

    // Footer (total + checkout) sits outside cart-body and may appear or
    // disappear entirely as the cart empties, so handle all four cases.
    var freshFooter = parsed.querySelector('.cart-drawer__footer');
    var liveFooter = document.querySelector('.cart-drawer__footer');
    if (freshFooter && liveFooter) {
      liveFooter.parentNode.replaceChild(freshFooter, liveFooter);
    } else if (freshFooter && !liveFooter && cartDrawer) {
      cartDrawer.appendChild(freshFooter);
    } else if (!freshFooter && liveFooter) {
      liveFooter.parentNode.removeChild(liveFooter);
    }
  }

  /**
   * Fetch the drawer on its own. Used after quantity changes, where we don't
   * already have section markup in hand.
   */
  function refreshCartDrawer() {
    return fetch(window.kefi.routes.cart + '?section_id=' + CART_DRAWER_SECTION, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart drawer refresh failed');
        return r.text();
      })
      .then(applyCartDrawerHTML)
      .catch(function () {
        console.warn('[KEFI] Cart drawer could not be refreshed.');
      });
  }

  // Cart quantity change (throttled to prevent rapid-fire API calls)
  document.addEventListener('click', throttle(function (e) {
    var btn = e.target.closest('.cart-item__qty-btn');
    if (!btn) return;
    var key    = btn.dataset.key;
    var action = btn.dataset.action;
    var qtyEl  = btn.parentElement ? btn.parentElement.querySelector('span') : null;
    var qty    = parseInt(qtyEl ? qtyEl.textContent : '1', 10);
    var newQty = action === 'plus' ? qty + 1 : Math.max(0, qty - 1);

    // Disable both buttons during request to prevent double-submit
    var parent = btn.closest('.cart-item__qty');
    var allBtns = parent ? parent.querySelectorAll('.cart-item__qty-btn') : [];
    allBtns.forEach(function (b) { b.disabled = true; });

    cartFetch(window.kefi.routes.cartChange, 'POST', { id: key, quantity: newQty })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart update failed');
        return r.json();
      })
      .then(function (data) {
        updateCartCount(data.item_count);
        if (qtyEl) safeText(qtyEl, String(newQty));
        // Re-render so line prices and the drawer total stay in sync
        return refreshCartDrawer();
      })
      .catch(function () {
        // Generic error — no internal details exposed (OWASP A09)
        console.warn('[KEFI] Cart update could not be completed.');
      })
      .finally(function () {
        allBtns.forEach(function (b) { b.disabled = false; });
      });
  }, 500));

  /* ============================================================
     CART PAGE — quantity +/- buttons

     Progressive enhancement: the underlying updates[] inputs post with the
     form when JS is unavailable. With JS, we adjust the input and submit so
     the merchant doesn't have to press "Update" separately.
     ============================================================ */

  document.addEventListener('click', throttle(function (e) {
    var btn = e.target.closest('[data-cart-minus], [data-cart-plus]');
    if (!btn) return;

    var row = btn.closest('.cart-page__item');
    var input = row ? row.querySelector('.cart-page__qty-input') : null;
    if (!input) return;

    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = 0;

    var isPlus = btn.hasAttribute('data-cart-plus');
    input.value = isPlus ? current + 1 : Math.max(0, current - 1);

    var form = document.getElementById('CartForm');
    if (form) {
      // requestSubmit keeps native validation; fall back for older browsers
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }
  }, 300));

  /* ============================================================
     ADD TO CART (product page)
     ============================================================ */

  var productForm = document.getElementById('product-form');
  if (productForm) {
    productForm.addEventListener('submit', function (e) {
      e.preventDefault();

      var addBtn = document.getElementById('add-to-cart');
      if (addBtn && addBtn.disabled) return; // Prevent double-submit

      /* Remember what the button actually said, rather than assuming it said
         "Add to Cart". Restoring it from data-label-available afterwards is
         what turned a SOLD OUT button into an ADD TO CART button the moment
         it was clicked — the label is the button's state, so putting back a
         different one silently changes what the page claims. */
      var addBtnLabel = addBtn ? addBtn.textContent.trim() : '';

      // Disable button immediately (rate limiting — OWASP A07)
      if (addBtn) {
        addBtn.disabled = true;
        safeText(addBtn, 'Adding...');
      }

      var formData = new FormData(productForm);

      // Bundled section rendering: ask /cart/add.js to return the re-rendered
      // drawer in the same response, so adding to cart is one round trip
      // instead of add → cart.js → section fetch.
      formData.append('sections', CART_DRAWER_SECTION);

      fetch(window.kefi.routes.cartAdd, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
        .then(function (r) {
          if (!r.ok) throw new Error('Add to cart failed: ' + r.status);
          return r.json();
        })
        .then(function (data) {
          var html = data.sections && data.sections[CART_DRAWER_SECTION];
          if (html) {
            applyCartDrawerHTML(html);
            updateCartCount(countItemsInDrawer());
            openCart();
            return;
          }
          // Fallback if bundled sections are unavailable
          return refreshCartDrawer()
            .then(function () {
              return cartFetch(window.kefi.routes.cart + '.js', 'GET', null);
            })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              updateCartCount(cart.item_count);
              openCart();
            });
        })
        .catch(function () {
          console.warn('[KEFI] Could not add item to cart.');
        })
        .finally(function () {
          if (addBtn) {
            addBtn.disabled = false;
            safeText(addBtn, addBtnLabel || addBtn.dataset.labelAvailable || 'Add to Cart');
          }
        });
    });
  }

  /* ============================================================
     QUICK ADD (product cards)

     Only rendered for single-variant products — anything with options links
     through to the product page instead, so we never guess a variant on the
     customer's behalf.
     ============================================================ */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn || btn.disabled) return;

    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    var original = btn.textContent;
    btn.disabled = true;
    safeText(btn, 'Adding...');

    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    body.append('sections', CART_DRAWER_SECTION);

    fetch(window.kefi.routes.cartAdd, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Quick add failed');
        return r.json();
      })
      .then(function (data) {
        var html = data.sections && data.sections[CART_DRAWER_SECTION];
        if (html) {
          applyCartDrawerHTML(html);
          updateCartCount(countItemsInDrawer());
          openCart();
          return;
        }
        return refreshCartDrawer().then(function () {
          updateCartCount(countItemsInDrawer());
          openCart();
        });
      })
      .catch(function () {
        console.warn('[KEFI] Could not add item to cart.');
      })
      .finally(function () {
        btn.disabled = false;
        safeText(btn, original);
      });
  });

  /* ============================================================
     RELATED PRODUCTS — Shopify recommendations

     The section renders empty on first paint; the `recommendations` object is
     only populated when fetched with a product_id via the Section Rendering
     API. We swap the fetched markup in once it arrives.
     ============================================================ */

  var relatedEl = document.getElementById('related-products');
  if (relatedEl && relatedEl.dataset.productId) {
    var relUrl = relatedEl.dataset.url + '?section_id=' + relatedEl.dataset.sectionId +
                 '&product_id=' + relatedEl.dataset.productId +
                 '&limit=' + (relatedEl.dataset.limit || 4);

    fetch(relUrl, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('Recommendations unavailable');
        return r.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.getElementById('related-products');
        // No recommendations for this product — leave the shell empty
        if (!fresh || !fresh.querySelector('.product-card')) return;
        relatedEl.parentNode.replaceChild(fresh, relatedEl);
      })
      .catch(function () {
        // Silent: related products are an enhancement, not core to the page
      });
  }

  /* ============================================================
     PRODUCT VARIANT SWATCHES
     ============================================================ */

  var swatches    = document.querySelectorAll('.swatch-btn');
  var variantInput = document.getElementById('variant-id');
  var productJsonEl = document.getElementById('product-json');

  // Parse product JSON safely — OWASP A03: never eval(), use JSON.parse
  var productData = null;
  if (productJsonEl) {
    try {
      productData = JSON.parse(productJsonEl.textContent);
    } catch (e) {
      console.warn('[KEFI] Could not parse product data.');
    }
  }

  function findVariant(selectedOptions) {
    if (!productData || !productData.variants) return null;
    return productData.variants.find(function (v) {
      return v.options.every(function (o, i) {
        return o === selectedOptions[i];
      });
    }) || null;
  }

  swatches.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var optionName = btn.dataset.optionName;

      // Update active state
      document.querySelectorAll('.swatch-btn[data-option-name="' + optionName + '"]')
        .forEach(function (b) {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
      btn.classList.add('active');
      btn.setAttribute('aria-pressed', 'true');

      // Update displayed selected value label
      var optionIndex = btn.dataset.optionIndex;
      var selectedLabel = document.getElementById('option-value-' + optionIndex);
      if (selectedLabel) safeText(selectedLabel, btn.dataset.optionValue); // textContent — safe

      // Collect all selected options
      var opts = [];
      document.querySelectorAll('.product-option').forEach(function (opt) {
        var active = opt.querySelector('.swatch-btn.active');
        if (active) opts.push(active.dataset.optionValue);
      });

      var variant = findVariant(opts);
      if (!variant) return;

      // Update hidden input
      if (variantInput) variantInput.value = variant.id;

      // Update price — use textContent, never innerHTML (OWASP A03)
      var priceEl = document.getElementById('product-price');
      if (priceEl) {
        // Clear existing content safely
        while (priceEl.firstChild) priceEl.removeChild(priceEl.firstChild);

        /* A zero-priced variant is bespoke/unpriced, not free. Formatting it
           would print "₹0.00" and undo the "Price on request" the server
           rendered. The wording comes from the element's own data attribute so
           it stays in sync with locales/en.default.json. */
        if (!variant.price) {
          var requestSpan = document.createElement('span');
          requestSpan.className = 'product-info__price-current';
          safeText(requestSpan, priceEl.dataset.priceOnRequest || 'Price on request');
          priceEl.appendChild(requestSpan);
        } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          var compareSpan = document.createElement('span');
          compareSpan.className = 'product-info__price-compare';
          safeText(compareSpan, formatMoney(variant.compare_at_price));
          var saleSpan = document.createElement('span');
          saleSpan.className = 'product-info__price-sale';
          safeText(saleSpan, formatMoney(variant.price));
          priceEl.appendChild(compareSpan);
          priceEl.appendChild(document.createTextNode(' '));
          priceEl.appendChild(saleSpan);
        } else {
          var currentSpan = document.createElement('span');
          currentSpan.className = 'product-info__price-current';
          safeText(currentSpan, formatMoney(variant.price));
          priceEl.appendChild(currentSpan);
        }
      }

      // Update add-to-cart button — labels come from the button's own data
      // attributes so they stay in sync with locales/en.default.json
      var addBtn = document.getElementById('add-to-cart');
      if (addBtn) {
        addBtn.disabled = !variant.available;
        safeText(
          addBtn,
          variant.available
            ? (addBtn.dataset.labelAvailable || 'Add to Cart')
            : (addBtn.dataset.labelSoldOut || 'Sold Out')
        );
      }

      // Pull this variant's photo to the front of the gallery grid, and move
      // the view to it — this is a deliberate click, so movement is wanted.
      showVariantImage(variant, true);

      // The material line differs per finish ("24K Gold bath" vs
      // "Platinum-Rhodium bath"), so it has to follow the selection.
      var noteEl = document.getElementById('product-material-note');
      if (noteEl) {
        if (variant.material_note) {
          // A per-variant metafield is the explicit answer; nothing to derive.
          safeText(noteEl, variant.material_note);
        } else {
          safeText(noteEl, resolveFinishWording(noteEl, variant));
        }
      }
    });
  });

  /**
   * Filter the gallery for whichever finish is selected ON PAGE LOAD.
   *
   * Without this the gallery only ever filtered after a swatch was clicked,
   * so the default view (Gold, here) showed every photo including the other
   * finishes — the filtering appeared broken until you interacted with it.
   */
  if (swatches.length && productData) {
    var initialOpts = [];
    document.querySelectorAll('.product-option').forEach(function (opt) {
      var active = opt.querySelector('.swatch-btn.active');
      if (active) initialOpts.push(active.dataset.optionValue);
    });
    var initialVariant = findVariant(initialOpts);
    if (initialVariant) showVariantImage(initialVariant);
  }

  /* ============================================================
     FORMAT MONEY — no innerHTML, uses shop currency from frozen global
     ============================================================ */

  /**
   * Which product option is "Finish"? Returns -1 if this product has no such
   * option (single-variant products, or ones using different option names).
   */
  function findFinishOptionIndex() {
    var found = -1;
    document.querySelectorAll('.product-option[data-option-index]').forEach(function (el) {
      if (found === -1 && (el.dataset.optionName || '').toLowerCase() === 'finish') {
        found = parseInt(el.dataset.optionIndex, 10);
      }
    });
    return found;
  }

  /**
   * Does this specific product have a genuine "Chrome" finish of its own?
   * Five products in this catalogue do (Pallas Bold Studs, the Senshii
   * ring/studs, Cosmos Studs Pearls) — everywhere else, "chrome" or "silver"
   * in a filename is just describing the LOOK of the Platinum-Rhodium
   * plating, not a separate variant. Mirrors FINISH_ALIASES in
   * woo_to_shopify.py: alias only when the product has no literal match.
   */
  function productHasFinish(finishIndex, label) {
    if (finishIndex < 0 || !productData || !productData.variants) return false;
    return productData.variants.some(function (v) {
      return (v.options[finishIndex] || '').toLowerCase() === label;
    });
  }

  /**
   * Update the gallery for the selected variant — two layers, applied in
   * order:
   *
   * 1. FILTER — hide any tile whose filename-derived data-finish-hint
   *    doesn't match the selected Finish. A tile with NO hint (no keyword
   *    found in its filename — roughly 60% of this catalogue's images)
   *    always stays visible, which is what keeps every product safe by
   *    default: nothing is ever hidden on a guess, only on a positive match.
   *
   * 2. PROMOTE — among whatever is left visible, move the variant's own
   *    designated cover photo (variant.featured_image, the one thing the
   *    CSV import always sets) to the front via CSS `order`.
   */
  /**
   * Reduce a finish to the metal it actually is.
   *
   * The two sides of this comparison never spelled the same thing the same
   * way: an option value reads "Platinum - Rhodium Plating" while a hint
   * reads "platinum". The old code compared them raw, so `hint === finish`
   * was almost never true, and the alias branch tested
   * `finishValue === 'platinum'` — which a value of "platinum - rhodium
   * plating" can never satisfy. The filter looked implemented and did
   * nothing.
   *
   * Normalising both sides first is what makes it work regardless of how a
   * finish happens to be worded. Rose gold is tested before gold, or it
   * would match the gold branch.
   */
  /**
   * Narrow the material line to the finish actually selected.
   *
   * A merged two-finish product carries one product-level tagline, so it has
   * to cover both: "…plated in a 24K Gold or Platinum-Rhodium bath…". True,
   * but it says nothing about what the customer is looking at, and it does
   * not change when they click Platinum.
   *
   * The per-variant metafield custom.material_note is the explicit answer and
   * wins wherever it is set. This is the fallback for the ~25 products where
   * it is not: the "A or B" phrase is one this theme's own import wrote, so
   * substituting it is safe rather than guesswork.
   *
   * Fails soft in every direction — no finish option, an unrecognised value,
   * or a tagline that never contained the phrase all return the text
   * unchanged. It can narrow the sentence or leave it; it cannot break it.
   */
  // [phrase to find, gold wording, platinum wording] — most specific first,
  // so "both A and B" is caught before a bare "A and B" would leave a
  // dangling "both".
  //
  // MUST match finish_rules in sections/main-product.liquid exactly. Liquid
  // renders the first paint, this rewrites it on every swatch click; if the
  // two tables disagree the line changes on click and reverts on reload.
  //
  // Seven rows because the catalogue has seven ways of saying "both
  // finishes". Only the second one used to be handled, which is why the
  // sentence stayed generic on most products no matter what was selected.
  var FINISH_RULES = [
    ['both 24K Gold and Platinum-Rhodium',  '24K Gold',        'Platinum-Rhodium'],
    ['24K Gold or Platinum-Rhodium',        '24K Gold',        'Platinum-Rhodium'],
    ['Platinum-Rhodium or 24K Gold',        '24K Gold',        'Platinum-Rhodium'],
    ['22K Gold or Platinum & Rhodium',      '22K Gold',        'Platinum & Rhodium'],
    ['24K gold-plating/Platinum & rhodium', '24K gold-plating', 'Platinum & rhodium'],
    ['22K gold-plating/Platinum & rhodium', '22K gold-plating', 'Platinum & rhodium'],
    ['22K gold-plating/rhodium',            '22K gold-plating', 'rhodium plating']
  ];

  function resolveFinishWording(el, variant) {
    // data-tagline-base holds the ORIGINAL sentence. Reading the element's
    // current text instead would compound: narrow to gold once and the "or"
    // is gone, so switching to platinum would have nothing left to replace.
    var base = el.getAttribute('data-tagline-base') || el.textContent || '';

    var idx = findFinishOptionIndex();
    if (idx < 0 || !variant || !variant.options) return base;

    var raw = (variant.options[idx] || '').toLowerCase();

    // Values that mean BOTH finishes at once — "Dual Tone", and
    // "Gold Plating, Rhodium & Platinum Plating" — describe a piece that
    // really does carry both, so the sentence is already correct and must
    // not be narrowed to either one.
    var saysGold = raw.indexOf('gold') > -1;
    var saysPlat = raw.indexOf('platinum') > -1 || raw.indexOf('rhodium') > -1;
    if (raw.indexOf('dual') > -1 || (saysGold && saysPlat)) return base;

    var token = finishToken(variant.options[idx]);
    var col;
    if (token === 'gold' || token === 'rose') {
      col = 1;
    } else if (token === 'platinum' || token === 'chrome') {
      col = 2;
    } else {
      // 'silver' lands here on purpose. "Sterling Silver" is a real option
      // value in this catalogue, not a synonym for Platinum-Rhodium, and
      // rewriting the line to say platinum would be a false material claim
      // on a solid silver piece. Leaving the sentence whole is the safe miss.
      return base;
    }

    for (var i = 0; i < FINISH_RULES.length; i++) {
      if (base.indexOf(FINISH_RULES[i][0]) > -1) {
        return base.split(FINISH_RULES[i][0]).join(FINISH_RULES[i][col]);
      }
    }
    return base;
  }

  function finishToken(s) {
    s = (s || '').toLowerCase();
    if (!s) return '';
    if (s.indexOf('rose') > -1 && s.indexOf('gold') > -1) return 'rose';
    if (s.indexOf('platinum') > -1 || s.indexOf('rhodium') > -1) return 'platinum';
    if (s.indexOf('chrome') > -1) return 'chrome';
    if (s.indexOf('silver') > -1) return 'silver';
    if (s.indexOf('gold') > -1) return 'gold';
    return '';
  }

  // `scroll` is opt-in per call. The page-load pass below must never
  // scroll: landing on a product and being thrown down to the gallery is
  // disorienting, and on a phone it would hide the title and price. Only a
  // deliberate swatch click asks for movement.
  function showVariantImage(variant, scroll) {
    var gallery = document.querySelector('.product-gallery');
    if (!gallery) return;

    var tiles = gallery.querySelectorAll('.product-gallery__img');
    if (!tiles.length) return;

    var finishIndex = findFinishOptionIndex();
    var finishValue = (variant && finishIndex > -1 && variant.options)
      ? finishToken(variant.options[finishIndex])
      : '';
    var hasChromeOfOwn = productHasFinish(finishIndex, 'chrome');

    tiles.forEach(function (tile) {
      var hint = finishToken(tile.dataset.finishHint);
      var visible = true;

      if (hint && finishValue) {
        if (hint === finishValue) {
          visible = true;
        } else if (
          finishValue === 'platinum' &&
          (hint === 'chrome' || hint === 'silver') &&
          !hasChromeOfOwn
        ) {
          // No Chrome variant of its own — "chrome"/"silver" photos are the
          // Platinum-Rhodium finish, just photographed/named differently.
          visible = true;
        } else {
          visible = false;
        }
      }

      tile.style.display = visible ? '' : 'none';
      tile.style.order = '';
    });

    if (!variant || !variant.featured_image || !variant.featured_image.id) return;

    var match = gallery.querySelector(
      '[data-image-id="' + String(variant.featured_image.id) + '"]'
    );
    if (!match || match.style.display === 'none') return;
    match.style.order = '-1';

    // Bring it into view.
    //
    // Promoting to order:-1 is enough on a product whose photos all change
    // with the finish — the new first tile is already where you are looking.
    // It is NOT enough on the celebrity products, where several editorial
    // shots sit above the piece itself: the finish photo is promoted past
    // them but can still be well below the fold, so switching swatch looks
    // like it did nothing.
    //
    // block:'nearest' is doing the work. It moves the page the minimum
    // distance needed and NOTHING AT ALL if the tile is already on screen,
    // so this never yanks the page away from someone reading the description.
    // inline:'center' is what scrolls the phone's horizontal photo strip,
    // where the gallery is a scroll-snap row rather than a grid.
    if (!scroll) return;
    var reduce = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    match.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var symbol = window.kefi && window.kefi.shopCurrency === 'INR' ? '₹' : '$';
    return symbol + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ============================================================
     HERO SLIDER
     ============================================================ */

  var heroTrack  = document.getElementById('hero-track');
  var heroDots   = document.querySelectorAll('.hero-slider__dot');
  var currentSlide = 0;
  var slideCount   = heroDots.length;
  var autoPlayTimer;

  function goToSlide(n) {
    currentSlide = ((n % slideCount) + slideCount) % slideCount;
    if (heroTrack) {
      heroTrack.style.transform = 'translateX(-' + currentSlide * 100 + '%)';
    }
    heroDots.forEach(function (d, i) {
      var active = i === currentSlide;
      d.classList.toggle('active', active);
      d.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  heroDots.forEach(function (dot, i) {
    dot.addEventListener('click', function () {
      goToSlide(i);
      resetAutoPlay();
    });
  });

  function startAutoPlay() {
    if (slideCount < 2) return;
    autoPlayTimer = setInterval(function () { goToSlide(currentSlide + 1); }, 5000);
  }
  function resetAutoPlay() {
    clearInterval(autoPlayTimer);
    startAutoPlay();
  }

  // Pause on reduced-motion preference (accessibility + performance)
  if (!prefersReducedMotion.matches) {
    startAutoPlay();
  }

  /* ============================================================
     HOT SELLERS — one product per view, alternating spread

     Each .hot-seller is 100% of the viewport width, so stepping is a whole
     slide at a time. Width is measured from the element rather than assumed,
     which keeps it correct when the layout stacks on mobile.
     ============================================================ */

  var hotTrack = document.getElementById('hot-track');
  if (hotTrack) {
    var hotPrev = document.getElementById('hot-prev');
    var hotNext = document.getElementById('hot-next');
    var hotIdx = 0;

    var hotSlides = function () {
      return hotTrack.querySelectorAll('.hot-seller');
    };

    /* On phones the card variant becomes a natively scrolling strip
       (scroll-snap, see .hot-sellers--cards in the CSS). Transforming the
       track there would fight the browser's own scrolling, so detect the mode
       from the viewport's computed overflow rather than duplicating the
       breakpoint here — the CSS stays the single source of truth. */
    var hotScrollMode = function () {
      var vp = hotTrack.parentElement;
      if (!vp) return false;
      return window.getComputedStyle(vp).overflowX === 'auto';
    };

    var hotUpdate = function () {
      var slides = hotSlides();
      if (!slides.length) return;

      if (hotScrollMode()) {
        hotTrack.style.transform = '';
        if (hotPrev) hotPrev.style.display = 'none';
        if (hotNext) hotNext.style.display = 'none';
        return;
      }

      var maxIdx = slides.length - 1;
      hotIdx = Math.max(0, Math.min(hotIdx, maxIdx));

      var slideWidth = slides[0].getBoundingClientRect().width;
      hotTrack.style.transform = 'translateX(-' + hotIdx * slideWidth + 'px)';

      // A single product needs no controls at all
      var many = slides.length > 1;
      if (hotPrev) {
        hotPrev.style.display = many ? '' : 'none';
        hotPrev.disabled = hotIdx <= 0;
      }
      if (hotNext) {
        hotNext.style.display = many ? '' : 'none';
        hotNext.disabled = hotIdx >= maxIdx;
      }
    };

    if (hotPrev) hotPrev.addEventListener('click', function () { hotIdx -= 1; hotUpdate(); });
    if (hotNext) hotNext.addEventListener('click', function () { hotIdx += 1; hotUpdate(); });

    window.addEventListener('resize', onFrame(hotUpdate), { passive: true });
    hotUpdate();
  }

  /* ============================================================
     INSTAGRAM STRIP CAROUSEL

     Same stepping logic as the product carousel, but sized off the first
     item's real width so it stays correct across the responsive breakpoints
     (4-up desktop / 3-up tablet / 2-up mobile) without duplicating them here.
     ============================================================ */

  var igTrack = document.getElementById('instagram-track');
  if (igTrack) {
    var igPrev = document.getElementById('instagram-prev');
    var igNext = document.getElementById('instagram-next');
    var igIdx = 0;

    var igItems = function () {
      return igTrack.querySelectorAll('.instagram-feed__item');
    };

    var igPerView = function () {
      var items = igItems();
      if (!items.length) return 1;
      var viewport = igTrack.parentElement;
      if (!viewport) return 1;
      var itemWidth = items[0].getBoundingClientRect().width;
      if (!itemWidth) return 1;
      return Math.max(1, Math.round(viewport.getBoundingClientRect().width / itemWidth));
    };

    var igMaxIdx = function () {
      return Math.max(0, igItems().length - igPerView());
    };

    var igUpdate = function () {
      var items = igItems();
      if (!items.length) return;
      var itemWidth = items[0].getBoundingClientRect().width;
      igIdx = Math.max(0, Math.min(igIdx, igMaxIdx()));
      igTrack.style.transform = 'translateX(-' + igIdx * itemWidth + 'px)';

      // Hide the arrows entirely when everything already fits
      var overflows = items.length > igPerView();
      if (igPrev) {
        igPrev.style.display = overflows ? '' : 'none';
        igPrev.disabled = igIdx <= 0;
      }
      if (igNext) {
        igNext.style.display = overflows ? '' : 'none';
        igNext.disabled = igIdx >= igMaxIdx();
      }
    };

    if (igPrev) igPrev.addEventListener('click', function () { igIdx -= 1; igUpdate(); });
    if (igNext) igNext.addEventListener('click', function () { igIdx += 1; igUpdate(); });

    window.addEventListener('resize', onFrame(igUpdate), { passive: true });
    igUpdate();

    /* Automatic feed integration. The endpoint is a server-side Worker, so
       Instagram credentials never reach the browser or the Shopify theme. */
    var igEndpoint = igTrack.dataset.instagramFeedEndpoint;
    if (igEndpoint) {
      var igEndpointUrl;
      try {
        igEndpointUrl = new URL(igEndpoint);
      } catch (e) {
        igEndpointUrl = null;
      }

      if (igEndpointUrl && igEndpointUrl.protocol === 'https:') {
        fetch(igEndpointUrl.href, {
          method: 'GET',
          credentials: 'omit',
          headers: { 'Accept': 'application/json' }
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Instagram feed unavailable');
            return response.json();
          })
          .then(function (payload) {
            if (!payload || !Array.isArray(payload.data)) return;

            var fragment = document.createDocumentFragment();
            var postsAdded = 0;
            payload.data.forEach(function (post) {
              if (!post || typeof post.image_url !== 'string' || typeof post.permalink !== 'string') return;

              var imageUrl, postUrl;
              try {
                imageUrl = new URL(post.image_url);
                postUrl = new URL(post.permalink);
              } catch (e) {
                return;
              }

              var isInstagramPost = postUrl.protocol === 'https:' &&
                (postUrl.hostname === 'instagram.com' || postUrl.hostname.endsWith('.instagram.com'));
              if (imageUrl.protocol !== 'https:' || !isInstagramPost) return;

              var link = document.createElement('a');
              link.className = 'instagram-feed__item';
              link.href = postUrl.href;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              link.setAttribute('aria-label', 'View Instagram post ' + (postsAdded + 1));

              var image = document.createElement('img');
              image.src = imageUrl.href;
              image.alt = 'Instagram post from KEFI jewellery';
              image.width = 500;
              image.height = 500;
              image.loading = 'lazy';
              link.appendChild(image);
              fragment.appendChild(link);
              postsAdded += 1;
            });

            if (postsAdded) {
              igTrack.replaceChildren(fragment);
              igIdx = 0;
              igUpdate();
            }
          })
          .catch(function () {
            /* Keep the manually configured posts visible as the safe fallback. */
          });
      }
    }
  }

  /* ============================================================
     NEWSLETTER POPUP
     Security:
     - Honeypot check (bot detection) — OWASP A07
     - Email validation before submit — OWASP A04
     - Rate limiting (button disabled during submit)
     - localStorage flag to avoid repeat harassment
     - Error messages use textContent, never innerHTML — OWASP A03
     ============================================================ */

  var popupOverlay = document.getElementById('newsletter-overlay');
  var popupClose   = document.getElementById('popup-close');
  var popupForm    = document.getElementById('popup-newsletter-form');
  var popupSubmit  = document.getElementById('popup-submit');
  var popupEmailEl = document.getElementById('popup-email');
  var popupError   = document.getElementById('popup-error');
  var POPUP_KEY    = 'kefi_nl_dismissed_v1';

  function showPopup() {
    // Don't show if already dismissed (localStorage — OWASP A07)
    if (sessionStorage.getItem(POPUP_KEY) || localStorage.getItem(POPUP_KEY)) return;
    if (!popupOverlay) return;
    popupOverlay.classList.add('active');
    popupOverlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (popupEmailEl) setTimeout(function () { popupEmailEl.focus(); }, 100);
  }

  function dismissPopup() {
    if (!popupOverlay) return;
    popupOverlay.classList.remove('active');
    popupOverlay.setAttribute('aria-hidden', 'true');
    localStorage.setItem(POPUP_KEY, '1');
    document.body.style.overflow = '';
  }

  // Delay is merchant-configurable (Theme settings > Newsletter popup)
  if (popupOverlay) {
    var popupDelay = parseInt(popupOverlay.dataset.delay, 10);
    if (isNaN(popupDelay) || popupDelay < 1) popupDelay = 5;
    setTimeout(showPopup, popupDelay * 1000);
  }

  if (popupClose) popupClose.addEventListener('click', dismissPopup);
  if (popupOverlay) {
    popupOverlay.addEventListener('click', function (e) {
      if (e.target === popupOverlay) dismissPopup();
    });
  }

  if (popupForm) {
    popupForm.addEventListener('submit', function (e) {
      hideError(popupError);

      // Honeypot check — OWASP A07
      if (isBot(popupForm)) {
        e.preventDefault();
        // Silently fail for bots; show success to not tip off scraper
        dismissPopup();
        return;
      }

      // Email validation — OWASP A04
      var email = popupEmailEl ? popupEmailEl.value.trim() : '';
      if (!isValidEmail(email)) {
        e.preventDefault();
        showError(popupError, 'Please enter a valid email address.');
        if (popupEmailEl) popupEmailEl.focus();
        return;
      }

      // Rate limit — disable button on submit (OWASP A07)
      if (popupSubmit) {
        popupSubmit.disabled = true;
        safeText(popupSubmit, 'Sending...');
      }

      // Shopify's {% form %} handles the actual POST with CSRF token —
      // we let the default form action proceed here.
      dismissPopup();
    });
  }

  // Footer newsletter form
  var footerForm   = document.getElementById('footer-newsletter-form');
  var footerSubmit = document.getElementById('footer-newsletter-submit');
  var footerEmail  = document.getElementById('footer-email');
  var footerError  = document.getElementById('footer-error');

  if (footerForm) {
    footerForm.addEventListener('submit', function (e) {
      hideError(footerError);

      // Honeypot
      if (isBot(footerForm)) {
        e.preventDefault();
        return;
      }

      // Email validation
      var email = footerEmail ? footerEmail.value.trim() : '';
      if (!isValidEmail(email)) {
        e.preventDefault();
        showError(footerError, 'Please enter a valid email address.');
        if (footerEmail) footerEmail.focus();
        return;
      }

      // Rate limit
      if (footerSubmit) {
        footerSubmit.disabled = true;
        safeText(footerSubmit, 'Sending...');
      }
    });
  }

  /* ============================================================
     ENGRAVING — live character counter

     The hard limit is the input's own maxlength (set per-product in Liquid
     from custom.engraving_max_chars), enforced natively by the browser —
     this only updates the "X/N" counter text as the customer types.
     ============================================================ */

  // Products can have SEVERAL engraving fields with different limits (e.g. a
  // 1-character initial plus a 6-character name), so this binds per field
  // rather than to a single element id.
  document.querySelectorAll('.product-engraving__input').forEach(function (input) {
    var wrap = input.closest('.product-engraving');
    var counter = wrap ? wrap.querySelector('.product-engraving__count') : null;
    if (!counter) return;
    input.addEventListener('input', function () {
      safeText(counter, String(input.value.length));
    });
  });

  /* ============================================================
     GIFT CARD RECIPIENT — "I want to send this as a gift"

     The fields ship DISABLED, and that is load-bearing rather than cosmetic:
     a disabled input is never submitted, so an unticked box means Shopify
     sees no recipient properties at all. If `Recipient email` were submitted
     empty, Shopify's gift-card validation would reject the whole add-to-cart
     and the customer could not buy the card for themselves.

     So this toggles `disabled`, not just visibility. Hiding them alone would
     still submit them.
     ============================================================ */
  var giftRoot = document.querySelector('[data-gift-recipient]');

  if (giftRoot) {
    var giftToggle = giftRoot.querySelector('[data-gift-recipient-toggle]');
    var giftFields = giftRoot.querySelector('[data-gift-recipient-fields]');
    var giftInputs = giftRoot.querySelectorAll('[data-gift-field]');
    var giftOffset = giftRoot.querySelector('[data-gift-offset]');

    /* Minutes this browser is behind UTC. Shopify uses it so a card
       scheduled for a date arrives on that date in the BUYER's timezone
       rather than whenever UTC reaches it — otherwise a card set for the
       14th from India lands on the 13th for a US recipient.
       getTimezoneOffset() returns minutes to ADD to local to reach UTC, which
       is already the sign Shopify expects. */
    if (giftOffset) giftOffset.value = String(new Date().getTimezoneOffset());

    var setGiftEnabled = function (on) {
      giftInputs.forEach(function (f) { f.disabled = !on; });
      if (giftFields) giftFields.hidden = !on;
      if (giftToggle) giftToggle.setAttribute('aria-expanded', on ? 'true' : 'false');
    };

    if (giftToggle) {
      giftToggle.addEventListener('change', function () {
        setGiftEnabled(giftToggle.checked);
        if (giftToggle.checked) {
          var email = giftRoot.querySelector('#gift-recipient-email');
          if (email) email.focus();
        }
      });
      // Browsers restore checkbox state on back-navigation without firing
      // change, which would leave a ticked box over disabled fields.
      setGiftEnabled(giftToggle.checked);
    }
  }

  /* ============================================================
     ENQUIRY MODAL — "Enquire Now" in place of Add to Cart

     This is a real page POST (Shopify's contact form), not an AJAX call, so
     there's no fetch here — just open/close chrome plus the same
     honeypot/validation/rate-limit pattern used on the other forms.

     If the merchant configured return_to on the form, Shopify redirects back
     to this same product page after submitting. The modal is opened by
     Liquid itself in that case (the "active" class is server-rendered when
     form.posted_successfully? or form.errors is true) — the JS below only
     needs to sync aria-hidden/focus for that already-open state.
     ============================================================ */

  var enquireBtn    = document.getElementById('enquire-now-btn');
  var enquiryModal  = document.getElementById('enquiry-modal');
  var enquiryClose  = document.getElementById('enquiry-modal-close');
  var enquiryForm   = document.getElementById('enquiry-form');
  var enquirySubmit = document.getElementById('enquiry-submit');
  var enquiryEmail  = document.getElementById('enquiry-email');
  var enquiryError  = document.getElementById('enquiry-error');

  function openEnquiry() {
    if (!enquiryModal) return;
    enquiryModal.classList.add('active');
    enquiryModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var firstField = enquiryModal.querySelector('input, textarea');
    if (firstField) setTimeout(function () { firstField.focus(); }, 80);
  }
  function closeEnquiry() {
    if (!enquiryModal) return;
    enquiryModal.classList.remove('active');
    enquiryModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (enquireBtn) enquireBtn.focus();
  }

  if (enquireBtn) enquireBtn.addEventListener('click', openEnquiry);
  if (enquiryClose) enquiryClose.addEventListener('click', closeEnquiry);
  if (enquiryModal) {
    enquiryModal.addEventListener('click', function (e) {
      if (e.target === enquiryModal) closeEnquiry();
    });
    // Server-rendered "active" (post-submit redirect) — sync focus/scroll lock
    if (enquiryModal.classList.contains('active')) openEnquiry();
  }

  if (enquiryForm) {
    enquiryForm.addEventListener('submit', function (e) {
      hideError(enquiryError);

      // Honeypot — OWASP A07
      if (isBot(enquiryForm)) {
        e.preventDefault();
        closeEnquiry();
        return;
      }

      // Email validation — OWASP A04
      var email = enquiryEmail ? enquiryEmail.value.trim() : '';
      if (!isValidEmail(email)) {
        e.preventDefault();
        showError(enquiryError, 'Please enter a valid email address.');
        if (enquiryEmail) enquiryEmail.focus();
        return;
      }

      // Rate limit — disable on submit (OWASP A07)
      if (enquirySubmit) {
        enquirySubmit.disabled = true;
        safeText(enquirySubmit, 'Sending...');
      }
    });
  }

  /* ============================================================
     SEARCH — validate input length (OWASP A04)
     ============================================================ */

  if (searchInput) {
    searchInput.setAttribute('maxlength', '200'); // Prevent excessively long queries
    var searchForm = searchInput.closest('form');
    if (searchForm) {
      searchForm.addEventListener('submit', function (e) {
        var q = searchInput.value.trim();
        if (q.length === 0) {
          e.preventDefault();
          searchInput.focus();
        }
        // Shopify's search handles server-side sanitisation
      });
    }
  }

  /* ============================================================
     KEYBOARD TRAP (Escape key closes all overlays) — Accessibility + UX
     ============================================================ */

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    closeSearch();
    closeCart();
    closeMobileNav();
    closeEnquiry();
    dismissPopup();
  });

  /* ============================================================
     INTERSECTION OBSERVER — animate-fade-up
     Only runs if reduced-motion not preferred
     ============================================================ */

  if (!prefersReducedMotion.matches && 'IntersectionObserver' in window) {
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = 'running';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    document.querySelectorAll('.animate-fade-up').forEach(function (el) {
      el.style.animationPlayState = 'paused';
      observer.observe(el);
    });
  }

  /* ============================================================
     STOREFRONT FILTERS (collection + search)

     Progressive enhancement only. The markup in snippets/facet-filters.liquid
     is a plain GET form that already works without JavaScript — here we add
     the full-screen panel behaviour on top.

     Selections are NOT auto-submitted. The panel covers the results, so
     reloading on every checkbox would close it out from under the customer;
     APPLY (a real submit button) is what commits them. SORT BY is the
     exception — it sits in the bar rather than the panel, so it still
     applies the moment it changes.

     The sort <select> lives OUTSIDE the <form> (form="facets-form" HTML
     association, same trick as the engraving field) so it can sit in the
     bar next to the Filter button rather than inside the panel. That means
     its change events never bubble to the form element — hence the separate
     listener on #sort-by below — but facetsForm.submit() still picks it up,
     since native form submission follows form associations regardless of
     DOM nesting.

     Empty inputs are disabled before submit so they don't end up in the URL
     as `filter.v.price.gte=` — Shopify treats those as active filters.
     ============================================================ */

  var facetsForm = document.getElementById('facets-form');

  if (facetsForm) {
    var facetsEl      = document.getElementById('facets');
    var facetsToggle  = document.getElementById('facets-toggle');
    var facetsCloseX  = document.getElementById('facets-close-x');
    var facetsCloseBt = document.getElementById('facets-close');
    var facetsHeader  = document.getElementById('site-header');
    var facetsDesktop = window.matchMedia('(min-width: 900px)');

    if (facetsEl) facetsEl.classList.add('facets--js');

    // Strip empty fields so they don't become blank query params
    function stripEmptyFacets() {
      facetsForm.querySelectorAll('input, select').forEach(function (f) {
        if (f.type !== 'checkbox' && f.value === '') f.disabled = true;
      });
    }
    facetsForm.addEventListener('submit', stripEmptyFacets);

    /* Dock the panel below whatever is actually occupying the top of the
       viewport, measured live rather than assumed. At the top of the page
       that's the full header; once scrolled it's the pinned nav row alone
       (the header itself scrolls away — see HEADER — SCROLL EFFECT above).
       Taking the max of both covers the handover between the two states. */
    function positionFacets() {
      var top = 0;
      if (facetsHeader) top = Math.max(top, facetsHeader.getBoundingClientRect().bottom);
      var pinnedNav = document.querySelector('.site-nav--pinned');
      if (pinnedNav) top = Math.max(top, pinnedNav.getBoundingClientRect().bottom);
      facetsForm.style.setProperty('--facets-top', top + 'px');
    }

    /* Desktop shows every group at once, so the headings have nothing to
       toggle and are disabled. Mobile restores the accordion, leaving groups
       with an active filter open so the customer can see what's applied. */
    function layoutFacetGroups() {
      var desktop = facetsDesktop.matches;
      facetsForm.querySelectorAll('.facet__toggle').forEach(function (btn) {
        var bodyId = btn.getAttribute('aria-controls');
        var body   = bodyId ? document.getElementById(bodyId) : null;
        var open   = desktop || btn.getAttribute('data-facet-active') === 'true';
        btn.disabled = desktop;
        btn.setAttribute('aria-expanded', String(open));
        if (body) body.setAttribute('aria-hidden', String(!open));
      });
    }

    facetsForm.querySelectorAll('.facet__toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var bodyId = btn.getAttribute('aria-controls');
        var body   = bodyId ? document.getElementById(bodyId) : null;
        var open   = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (body) body.setAttribute('aria-hidden', String(open));
      });
    });

    function openFacets() {
      positionFacets();
      layoutFacetGroups();
      facetsForm.classList.add('active');
      facetsForm.setAttribute('aria-hidden', 'false');
      if (facetsToggle) facetsToggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      var first = facetsForm.querySelector('.facet__toggle:not([disabled]), .facet__checkbox:not([disabled])');
      if (first) setTimeout(function () { first.focus(); }, 50);
    }

    function closeFacets() {
      facetsForm.classList.remove('active');
      facetsForm.setAttribute('aria-hidden', 'true');
      if (facetsToggle) { facetsToggle.setAttribute('aria-expanded', 'false'); facetsToggle.focus(); }
      document.body.style.overflow = '';
    }

    if (facetsToggle) {
      facetsToggle.addEventListener('click', function () {
        if (facetsForm.classList.contains('active')) closeFacets();
        else openFacets();
      });
    }
    if (facetsCloseX)  facetsCloseX.addEventListener('click', closeFacets);
    if (facetsCloseBt) facetsCloseBt.addEventListener('click', closeFacets);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && facetsForm.classList.contains('active')) closeFacets();
    });

    window.addEventListener('resize', onFrame(function () {
      if (facetsForm.classList.contains('active')) positionFacets();
    }));

    // Crossing the breakpoint swaps accordion <-> all-open, even while open
    if (facetsDesktop.addEventListener) facetsDesktop.addEventListener('change', layoutFacetGroups);
    else if (facetsDesktop.addListener) facetsDesktop.addListener(layoutFacetGroups);

    /* ---- SORT BY ----
       A text trigger with a floating panel of radios, not a <select>: a
       native popup is drawn by the OS and can't be styled to match.

       Sort applies immediately on change, unlike the filter panel — it sits
       in the bar, doesn't cover the results, and reloading is the point.

       The radios live outside the form (form="facets-form"), so their change
       events never bubble to it; the listener is delegated from the menu
       instead. Native submission still collects them, since it follows form
       association rather than DOM nesting. */
    var sortRoot = document.querySelector('[data-sort-root]');

    if (sortRoot) {
      var sortToggle = sortRoot.querySelector('[data-sort-toggle]');
      var sortMenu   = sortRoot.querySelector('[data-sort-menu]');

      var closeSort = function () {
        if (!sortRoot.classList.contains('facets-sort--open')) return;
        sortRoot.classList.remove('facets-sort--open');
        if (sortToggle) sortToggle.setAttribute('aria-expanded', 'false');
      };

      if (sortToggle) {
        sortToggle.addEventListener('click', function (e) {
          e.stopPropagation();
          var open = sortRoot.classList.toggle('facets-sort--open');
          sortToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }

      if (sortMenu) {
        // Clicks inside must not reach the document handler below, or the
        // panel would close before the radio registered.
        sortMenu.addEventListener('click', function (e) { e.stopPropagation(); });

        sortMenu.addEventListener('change', function (e) {
          if (!e.target || !e.target.hasAttribute('data-sort-option')) return;
          closeSort();
          stripEmptyFacets();
          facetsForm.submit();
        });
      }

      document.addEventListener('click', closeSort);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          if (!sortRoot.classList.contains('facets-sort--open')) return;
          closeSort();
          if (sortToggle) sortToggle.focus();
        }
      });
    }
  }

  /* ============================================================
     LOOKBOOK VIDEO

     The markup already autoplays: muted + loop + playsinline + no controls,
     which is what the design calls for and what works without JS. This adds
     two things on top.

     1. REDUCED MOTION. A silent clip looping forever is exactly what
        prefers-reduced-motion exists to suppress. Those visitors get the
        poster frame instead. No controls are added to compensate — the design
        deliberately has none — so this trades motion for a still image
        rather than for a dead black box.

     2. ONLY PLAY WHAT'S ON SCREEN. A lookbook stacks several clips; leaving
        them all decoding at once burns battery and bandwidth for video nobody
        is looking at. Each one plays when it scrolls into view and pauses
        when it leaves.
     ============================================================ */

  var lookbookVideos = document.querySelectorAll('[data-lookbook-video]');

  if (lookbookVideos.length) {
    if (prefersReducedMotion.matches) {
      lookbookVideos.forEach(function (v) {
        v.autoplay = false;
        v.removeAttribute('autoplay');
        v.pause();
        // Rewind so the poster (or first frame) is what's shown
        try { v.currentTime = 0; } catch (e) { /* not seekable yet */ }
      });
    } else if ('IntersectionObserver' in window) {
      var videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            // play() rejects if the browser still blocks autoplay; the poster
            // simply stays put, which is an acceptable outcome here.
            var p = v.play();
            if (p && typeof p.catch === 'function') p.catch(function () {});
          } else {
            v.pause();
          }
        });
      }, { threshold: 0.15 });

      lookbookVideos.forEach(function (v) { videoObserver.observe(v); });
    }
  }

  /* ============================================================
     INFINITE SCROLL / LOAD MORE (collection + search)

     Appends the next page of products into the existing grid instead of
     navigating to it. Progressive enhancement over the real numbered links
     in snippets/pagination.liquid — see that file for why they stay.

     ── WHY THIS DOESN'T COST ANYTHING ON LOAD ──────────────────────
     1. Nothing runs until the customer scrolls near the bottom. First paint,
        LCP and time-to-interactive are byte-for-byte what they were.

     2. IntersectionObserver, not a scroll listener. A scroll handler runs on
        every frame of every scroll for the life of the page — the single
        most common way infinite scroll makes a site feel heavy. The observer
        is evaluated by the browser off the main thread and calls back only
        when the boundary is actually crossed.

     3. The Section Rendering API returns one section, not a page. No header,
        footer, stylesheet or script is re-sent or re-parsed for a batch.

     4. Appended cards carry loading="lazy" and no fetchpriority (the sections
        pass `index` only on page 1), so their images are fetched by the
        browser on approach rather than all at once on append.

     5. rootMargin starts the request PREFETCH_PX before the boundary is on
        screen, so the batch has usually arrived by the time it would have
        been noticed. That is what makes it feel like there was never a page
        boundary at all.

     ── ORDERING ────────────────────────────────────────────────────
     Responses can arrive out of order if a customer flings the page, so a
     single in-flight flag gates the whole thing: one request at a time, and
     the next URL is only ever read from the response that was just appended.
     ============================================================ */

  var pgWrap = document.querySelector('[data-pagination]');

  if (pgWrap && pgWrap.dataset.paginationStyle !== 'numbers') {
    var pgGrid    = document.querySelector('[data-pagination-grid]');
    var pgBtn     = pgWrap.querySelector('[data-pagination-more]');
    var pgNumbers = pgWrap.querySelector('[data-pagination-numbers]');
    var pgStatus  = pgWrap.querySelector('[data-pagination-status]');

    /* Two conditions to enhance at all:

       pgGrid            — there has to be something to append into.

       data-pagination-next — there has to be a page left to fetch. Without it
         we are ON the last page, which is where a crawler link or a bookmark
         pointing at ?page=4 lands someone. Bailing out leaves the numbered
         links visible, which matters: hiding them there would remove the only
         way back to page 1 and put nothing in its place, since there is also
         nothing further to scroll for. Guarding on "has a next page" rather
         than on "is page 1" keeps the enhancement working for a mid-sequence
         landing, where there genuinely is more to load. */
    if (pgGrid && pgWrap.dataset.paginationNext) {
      var PG_PREFETCH_PX = 600;   // start fetching this far before the boundary
      var pgInfinite = pgWrap.dataset.paginationStyle === 'infinite';
      var pgLoading  = false;
      var pgObserver = null;

      /* The numbered links have done their job for a JS visitor — hide them.
         The `hidden` attribute rather than a class, since kefi-base.css makes
         [hidden] authoritative and this also removes them from the tab order
         and the accessibility tree, which a class alone would not. */
      if (pgNumbers) pgNumbers.hidden = true;

      // Manual mode shows the button immediately; infinite keeps it in
      // reserve and only reveals it if a fetch fails.
      if (pgBtn && !pgInfinite) pgBtn.hidden = false;

      function pgSay(msg) {
        if (pgStatus) pgStatus.textContent = msg;
      }

      /* No next URL left means the last page is in the DOM: tear the whole
         thing down so nothing keeps observing a boundary that can't move. */
      function pgFinish() {
        if (pgObserver) { pgObserver.disconnect(); pgObserver = null; }
        if (pgBtn) pgBtn.hidden = true;
        pgWrap.removeAttribute('data-pagination-next');
        // Collapse the spacing — everything inside is hidden now, and a tall
        // empty box above the footer reads as a broken layout.
        pgWrap.classList.add('pagination-wrap--done');
      }

      function pgLoadNext() {
        var next = pgWrap.dataset.paginationNext;
        if (pgLoading || !next) return;

        pgLoading = true;
        if (pgBtn) pgBtn.disabled = true;
        pgWrap.classList.add('pagination-wrap--loading');

        /* next already carries the active filters and sort — Shopify builds
           it from the live request — and always contains ?page=, so & is the
           correct separator here. */
        var url = next + '&section_id=' + encodeURIComponent(pgWrap.dataset.paginationSection);

        fetch(url, { credentials: 'same-origin' })
          .then(function (r) {
            if (!r.ok) throw new Error('Page unavailable');
            return r.text();
          })
          .then(function (html) {
            var parsed   = new DOMParser().parseFromString(html, 'text/html');
            var freshGrid = parsed.querySelector('[data-pagination-grid]');
            var freshPg   = parsed.querySelector('[data-pagination]');

            var added = 0;
            if (freshGrid) {
              /* Moving nodes across documents: adoptNode first, then append.
                 Built as a fragment so the grid reflows once for the batch
                 rather than once per card. */
              var frag = document.createDocumentFragment();
              while (freshGrid.firstElementChild) {
                frag.appendChild(document.adoptNode(freshGrid.firstElementChild));
                added++;
              }
              pgGrid.appendChild(frag);
            }

            /* The response's own pagination is the only source of truth for
               where we are — never increment a counter locally, or a page
               that redirects or gets filtered out silently desynchronises. */
            var nextUrl = freshPg && freshPg.getAttribute('data-pagination-next');
            if (nextUrl) {
              pgWrap.dataset.paginationNext = nextUrl;
            } else {
              pgFinish();
            }

            if (added) pgSay(pgWrap.dataset.labelLoaded || 'More products loaded.');
            pgLoading = false;
            if (pgBtn) pgBtn.disabled = false;
            pgWrap.classList.remove('pagination-wrap--loading');

            /* Two cases need another round immediately rather than waiting
               for a scroll that may never come:
                 - "Hide sold-out" can filter a whole page down to nothing, so
                   the page grew by 0 and the boundary never moved.
                 - A tall screen (or a fast fling) can leave the boundary still
                   inside the prefetch band after appending.
               IntersectionObserver does not re-fire for an element that was
               already intersecting, so it cannot notice either on its own. */
            if (pgInfinite) pgMaybeLoad();
          })
          .catch(function () {
            /* Leave the customer a way forward rather than a dead end: stop
               auto-loading and surface the button as a manual retry. The
               numbered links are still in the DOM and can be un-hidden by
               anyone who needs them. */
            pgLoading = false;
            pgWrap.classList.remove('pagination-wrap--loading');
            if (pgObserver) { pgObserver.disconnect(); pgObserver = null; }
            if (pgBtn) {
              pgBtn.hidden = false;
              pgBtn.disabled = false;
            }
            pgSay(pgWrap.dataset.labelError || 'Could not load more products.');
          });
      }

      // Is the boundary inside the prefetch band right now?
      function pgMaybeLoad() {
        if (pgLoading || !pgWrap.dataset.paginationNext) return;
        var box = pgWrap.getBoundingClientRect();
        if (box.top - PG_PREFETCH_PX <= window.innerHeight) pgLoadNext();
      }

      if (pgBtn) {
        pgBtn.addEventListener('click', function () {
          // A manual click after a failure should resume watching
          if (pgInfinite && !pgObserver) pgStartObserving();
          pgLoadNext();
        });
      }

      function pgStartObserving() {
        if (!('IntersectionObserver' in window)) return;
        pgObserver = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) pgLoadNext();
        }, { rootMargin: PG_PREFETCH_PX + 'px 0px' });
        pgObserver.observe(pgWrap);
      }

      if (pgInfinite) {
        if ('IntersectionObserver' in window) {
          pgStartObserving();
        } else {
          // Ancient browser: degrade to the button rather than to nothing
          if (pgBtn) pgBtn.hidden = false;
        }
      }
    }
  }

  /* ============================================================
     FREEZE PUBLIC GLOBALS — prevent prototype pollution (OWASP A08)
     ============================================================ */
  if (window.kefi) {
    try { Object.freeze(window.kefi); } catch (e) { /* already frozen */ }
  }

})();
